import propertyService from "reduxStore/service/propertyService";
import { isValidMongoObjectId, normalizeMongoId } from "utils/utils";

/**
 * Shared GRE banner blob loader.
 * Starts as soon as a property is selected (before GreBannerImage mounts) so the
 * heavy getPropertyDetail request does not starve the banner / OTP network path.
 */

const cache = new Map();

function emptyEntry() {
  return { status: "empty", objectUrl: null, promise: null };
}

function readyEntry(objectUrl) {
  return { status: "ready", objectUrl, promise: null };
}

export function getGreBannerCacheEntry(propertyId) {
  const id = normalizeMongoId(propertyId);
  if (!isValidMongoObjectId(id)) {
    return emptyEntry();
  }
  return cache.get(id) || null;
}

/**
 * @param {string} propertyId
 * @param {{ hasBanner?: boolean|null }} [options]
 *   hasBanner=false → skip network (list says no banner).
 *   hasBanner=true/null/undefined → fetch (or use cache).
 */
export function preloadGreBannerImage(propertyId, options = {}) {
  const id = normalizeMongoId(propertyId);
  if (!isValidMongoObjectId(id)) {
    return Promise.resolve(emptyEntry());
  }

  if (options.hasBanner === false) {
    const empty = emptyEntry();
    cache.set(id, empty);
    return Promise.resolve(empty);
  }

  const existing = cache.get(id);
  if (existing?.status === "ready" || existing?.status === "empty") {
    return Promise.resolve(existing);
  }
  if (existing?.promise) {
    return existing.promise;
  }

  const promise = propertyService
    .getGreBannerImageBlob({
      property_id: id,
      suppressGlobalLoader: true,
    })
    .then((response) => {
      if (response?.noBanner || !response?.data) {
        const empty = emptyEntry();
        cache.set(id, empty);
        return empty;
      }
      const objectUrl = URL.createObjectURL(response.data);
      const ready = readyEntry(objectUrl);
      cache.set(id, ready);
      return ready;
    })
    .catch(() => {
      const empty = emptyEntry();
      cache.set(id, empty);
      return empty;
    });

  cache.set(id, { status: "loading", objectUrl: null, promise });
  return promise;
}
